import { useEffect, useMemo, useState } from "react";
import { Check, Key, MagnifyingGlass, PencilSimple, Plus, Trash, UserCircle, WarningCircle, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, fileUrl, formatApiErrorDetail } from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import ImageEditorModal from "./ImageEditorModal";

const CLASS_OPTIONS = [
    "Tim Hijau (Pemula)",
    "Tim Kuning",
    "Tim Jingga",
    "Tim Biru",
    "Tim Pink",
    "Tim Merah",
    "Speed",
    "Slalom",
];

const emptyAthlete = {
    name: "",
    photo_url: "",
    nickname: "",
    class_name: "",
    status: "Aktif",
    gender: "",
    birth_place: "",
    birth_date: "",
    joined_year: new Date().getFullYear(),
    address: "",
    address_kk: "",
    religion: "",
    referral_source: "",
    promo_code: "",
};

function toInputDate(value) {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
    const match = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (!match) return "";
    const [, day, month, year] = match;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

export default function ReregistrationTab() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(null);
    const [selectedParent, setSelectedParent] = useState(null);
    const [selectedChild, setSelectedChild] = useState(null);
    const [accountForm, setAccountForm] = useState({ username: "", email: "", whatsapp: "", father_name: "", mother_name: "", status: "active" });
    const [athleteForm, setAthleteForm] = useState(emptyAthlete);
    const [passwordForm, setPasswordForm] = useState({ new_password: "" });
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [photoEditor, setPhotoEditor] = useState(null);
    const [lastPassword, setLastPassword] = useState(null);
    const [search, setSearch] = useState("");
    const [joinMonths, setJoinMonths] = useState({});
    const defaultJoinMonth = new Date(Date.now() + 8 * 3600e3).toISOString().slice(0, 7); // WITA

    const visibleItems = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return items;
        return items.filter((parent) =>
            (parent.username || "").toLowerCase().includes(q) ||
            (parent.email || "").toLowerCase().includes(q) ||
            (parent.children || []).some((child) => (child.name || "").toLowerCase().includes(q))
        );
    }, [items, search]);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/registrations");
            setItems(data);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const openAccount = (parent) => {
        setSelectedParent(parent);
        setAccountForm({
            username: parent.username || "",
            email: parent.email || "",
            whatsapp: parent.contact_phone || "",
            father_name: parent.father_name || "",
            mother_name: parent.mother_name || "",
            status: parent.status || "active",
        });
        setDialog("account");
    };

    const saveAccount = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await api.put(`/admin/parents/${selectedParent.id}`, accountForm);
            toast.success("Akun orang tua diperbarui");
            setDialog(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const openPassword = (parent) => {
        setSelectedParent(parent);
        setPasswordForm({ new_password: "" });
        setDialog("password");
    };

    const savePassword = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            const { data } = await api.post(`/admin/parents/${selectedParent.id}/password`, passwordForm);
            setLastPassword(data);
            toast.success("Password orang tua diperbarui");
            setDialog(null);
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const openAthlete = (parent, child = null) => {
        setSelectedParent(parent);
        setSelectedChild(child);
        setAthleteForm(child ? {
            name: child.name || "",
            photo_url: child.photo_url || "",
            nickname: child.nickname || "",
            class_name: child.class_name || "",
            status: child.status || "Aktif",
            gender: child.gender || "",
            birth_place: child.birth_place || "",
            birth_date: toInputDate(child.birth_date),
            joined_year: Number(child.joined_date || child.joined_raw || new Date().getFullYear()),
            address: child.address || "",
            address_kk: child.address_kk || "",
            religion: child.religion || "",
            referral_source: child.referral_source || "",
            promo_code: child.promo_code || "",
        } : emptyAthlete);
        setDialog("athlete");
    };

    const uploadPhoto = async (fileOrBlob) => {
        if (!fileOrBlob) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", fileOrBlob, "foto.jpg");
            const { data } = await api.post("/admin/upload", fd);
            setAthleteForm((prev) => ({ ...prev, photo_url: data.url }));
            toast.success("Foto terunggah");
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setUploading(false);
        }
    };

    const pickPhoto = (file) => {
        if (!file) return;
        setPhotoEditor({ src: URL.createObjectURL(file) });
    };

    const editExistingPhoto = async () => {
        try {
            const res = await fetch(fileUrl(athleteForm.photo_url));
            const blob = await res.blob();
            setPhotoEditor({ src: URL.createObjectURL(blob) });
        } catch {
            toast.error("Gagal memuat foto untuk diedit");
        }
    };

    const closePhotoEditor = () => {
        if (photoEditor?.src) URL.revokeObjectURL(photoEditor.src);
        setPhotoEditor(null);
    };

    const onPhotoEditorSave = async (blob) => {
        closePhotoEditor();
        await uploadPhoto(blob);
    };

    const saveAthlete = async (e) => {
        e.preventDefault();
        setSaving(true);
        const payload = { ...athleteForm, joined_year: Number(athleteForm.joined_year) };
        try {
            if (selectedChild) await api.put(`/admin/student-records/${selectedChild.id}`, payload);
            else await api.post(`/admin/parents/${selectedParent.id}/children`, payload);
            toast.success("Data atlet tersimpan");
            setDialog(null);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const removeAthlete = async (child) => {
        if (!window.confirm(`Hapus data atlet "${child.name}"? Data publik atlet terkait juga dihapus.`)) return;
        try {
            await api.delete(`/admin/student-records/${child.id}`);
            toast.success("Data atlet dihapus");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const removeParent = async (parent) => {
        if (!window.confirm(`Hapus akun "${parent.username}" beserta semua data atlet di dalamnya?`)) return;
        try {
            await api.delete(`/admin/parents/${parent.id}`);
            toast.success("Akun orang tua dihapus");
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const approveParent = async (parent) => {
        try {
            const { data } = await api.post(`/admin/registrations/${parent.id}/approve`, { join_month: joinMonths[parent.id] || defaultJoinMonth });
            toast.success(data.message || `Akun ${parent.username} disetujui`);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const rejectParent = async (parent) => {
        const reason = window.prompt("Alasan penolakan (opsional):", "") || "";
        try {
            await api.post(`/admin/registrations/${parent.id}/reject`, { reason });
            toast.success(`Akun ${parent.username} ditolak`);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const approveChild = async (child) => {
        try {
            const { data } = await api.post(`/admin/student-records/${child.id}/approve`, { join_month: joinMonths[child.id] || defaultJoinMonth });
            toast.success(data.message || `Atlet ${child.name} disetujui`);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    const rejectChild = async (child) => {
        const reason = window.prompt(`Alasan menolak penambahan ${child.name} (opsional):`, "") || "";
        try {
            await api.post(`/admin/student-records/${child.id}/reject`, { reason });
            toast.success(`Penambahan atlet ${child.name} ditolak`);
            load();
        } catch (err) {
            toast.error(formatApiErrorDetail(err.response?.data?.detail));
        }
    };

    return (
        <div className="space-y-8" data-testid="reregistration-admin-tab">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
                <div>
                    <h2 className="font-display uppercase text-2xl text-neutral-900">Akun Pendaftaran</h2>
                    <p className="text-sm text-racing-muted mt-2 max-w-3xl">
                        Kelola akun dari Daftar Ulang dan Daftar Siswa Baru. Siswa baru berstatus pending sampai admin memberi ACC; saat ACC, pilih bulan siswa mulai masuk klub — biaya pendaftaran & iuran bulan pertama otomatis tercatat lunas dan ceklist bulan sebelumnya ditandai "belum terdaftar".
                    </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-3 sm:items-center">
                    <div className="relative">
                        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-racing-muted" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Cari nama atlet / akun..."
                            data-testid="reregistration-search-input"
                            className="bg-white border border-racing-line pl-10 pr-4 py-3 text-sm w-full md:w-72 focus:outline-none focus:border-racing-red transition-colors"
                        />
                    </div>
                    <span className="text-xs uppercase tracking-[0.2em] text-racing-muted" data-testid="reregistration-account-count">
                        {items.length} akun • {items.filter((item) => item.status === "pending").length} akun pending • {items.reduce((total, item) => total + (item.pending_child_count || 0), 0)} anak pending
                    </span>
                </div>
            </div>

            {search && (
                <p className="text-xs text-racing-muted" data-testid="reregistration-search-count">{visibleItems.length} akun cocok dengan "{search}"</p>
            )}

            {lastPassword && (
                <div className="border border-racing-gold bg-racing-gold/10 p-5" data-testid="admin-new-password-result">
                    <p className="text-sm text-neutral-900">Password baru untuk <strong>{lastPassword.username}</strong>: <strong className="text-racing-red">{lastPassword.new_password}</strong></p>
                </div>
            )}

            {(() => {
                const pendingAccounts = visibleItems.filter((parent) => parent.status === "pending");
                const pendingChildren = [];
                visibleItems.forEach((parent) => {
                    if (parent.status !== "active") return;
                    (parent.children || []).forEach((child) => {
                        if (child.status === "Menunggu ACC") pendingChildren.push({ parent, child });
                    });
                });
                if (pendingAccounts.length === 0 && pendingChildren.length === 0 && search) return null;
                return (
                    <div className="grid lg:grid-cols-2 gap-6" data-testid="acc-queue-sections">
                        <section className="border-2 border-racing-gold bg-racing-gold/5" data-testid="acc-accounts-section">
                            <div className="px-5 py-3 border-b border-racing-gold/40 flex items-center justify-between">
                                <h3 className="font-display uppercase text-lg text-racing-golddeep">Akun Baru Menunggu ACC</h3>
                                <span className="font-display text-2xl text-racing-golddeep" data-testid="acc-accounts-count">{pendingAccounts.length}</span>
                            </div>
                            {pendingAccounts.length === 0 ? (
                                <p className="px-5 py-4 text-sm text-racing-muted" data-testid="acc-accounts-empty">Tidak ada akun baru yang menunggu.</p>
                            ) : pendingAccounts.map((parent, i) => (
                                <div key={parent.id} className="px-5 py-4 border-b border-racing-gold/30 last:border-0 flex flex-wrap items-center gap-3" data-testid={`acc-account-${i}`}>
                                    <div className="flex-1 min-w-[180px]">
                                        <p className="font-medium text-neutral-900">{parent.username}</p>
                                        <p className="text-xs text-racing-muted">{parent.email} • {(parent.children || []).length} anak</p>
                                    </div>
                                    <label className="flex items-center gap-2 border border-racing-line bg-white px-2 py-1.5" title="Bulan siswa mulai masuk klub">
                                        <span className="text-[9px] uppercase tracking-[0.15em] text-racing-muted">Bulan masuk</span>
                                        <input type="month" value={joinMonths[parent.id] || defaultJoinMonth} onChange={(e) => setJoinMonths({ ...joinMonths, [parent.id]: e.target.value })} data-testid={`acc-account-month-${i}`} className="bg-transparent text-xs focus:outline-none" />
                                    </label>
                                    <button onClick={() => approveParent(parent)} data-testid={`acc-account-approve-${i}`} className="flex items-center gap-1.5 bg-green-700 text-white px-3 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-green-800 transition-colors"><Check size={14} /> ACC</button>
                                    <button onClick={() => rejectParent(parent)} data-testid={`acc-account-reject-${i}`} className="flex items-center gap-1.5 border border-racing-red px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-red hover:bg-racing-red hover:text-white transition-colors"><X size={14} /> Tolak</button>
                                </div>
                            ))}
                        </section>
                        <section className="border-2 border-racing-gold bg-racing-gold/5" data-testid="acc-children-section">
                            <div className="px-5 py-3 border-b border-racing-gold/40 flex items-center justify-between">
                                <h3 className="font-display uppercase text-lg text-racing-golddeep">Tambahan Anak Menunggu ACC</h3>
                                <span className="font-display text-2xl text-racing-golddeep" data-testid="acc-children-count">{pendingChildren.length}</span>
                            </div>
                            {pendingChildren.length === 0 ? (
                                <p className="px-5 py-4 text-sm text-racing-muted" data-testid="acc-children-empty">Tidak ada tambahan anak yang menunggu.</p>
                            ) : pendingChildren.map(({ parent, child }, i) => (
                                <div key={child.id} className="px-5 py-4 border-b border-racing-gold/30 last:border-0 flex flex-wrap items-center gap-3" data-testid={`acc-child-${i}`}>
                                    <div className="flex-1 min-w-[180px]">
                                        <p className="font-medium text-neutral-900">{child.name}</p>
                                        <p className="text-xs text-racing-muted">{child.class_name || "—"} • anak dari {parent.username}</p>
                                    </div>
                                    <label className="flex items-center gap-2 border border-racing-line bg-white px-2 py-1.5" title="Bulan anak mulai masuk klub">
                                        <span className="text-[9px] uppercase tracking-[0.15em] text-racing-muted">Bulan masuk</span>
                                        <input type="month" value={joinMonths[child.id] || defaultJoinMonth} onChange={(e) => setJoinMonths({ ...joinMonths, [child.id]: e.target.value })} data-testid={`acc-child-month-${i}`} className="bg-transparent text-xs focus:outline-none" />
                                    </label>
                                    <button onClick={() => approveChild(child)} data-testid={`acc-child-approve-${i}`} className="flex items-center gap-1.5 bg-green-700 text-white px-3 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-green-800 transition-colors"><Check size={14} /> ACC Anak</button>
                                    <button onClick={() => rejectChild(child)} data-testid={`acc-child-reject-${i}`} className="flex items-center gap-1.5 border border-racing-red px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-red hover:bg-racing-red hover:text-white transition-colors"><X size={14} /> Tolak</button>
                                </div>
                            ))}
                        </section>
                    </div>
                );
            })()}

            <h3 className="text-[10px] uppercase tracking-[0.25em] text-racing-muted border-b border-racing-line pb-2" data-testid="reregistration-all-accounts-heading">Semua Akun Pendaftar</h3>

            {loading ? (
                <p className="text-racing-muted" data-testid="reregistration-loading">Memuat data daftar ulang...</p>
            ) : visibleItems.length === 0 ? (
                <div className="border border-racing-line bg-white p-10 text-center" data-testid="reregistration-empty">
                    <p className="text-racing-muted">{search ? `Tidak ada akun/atlet yang cocok dengan "${search}".` : "Belum ada akun pendaftaran/daftar ulang."}</p>
                </div>
            ) : (
                <div className="space-y-6" data-testid="reregistration-account-list">
                    {visibleItems.map((parent, index) => (
                        <section key={parent.id} className="bg-white border border-racing-line" data-testid={`reregistration-account-${index}`}>
                            <div className="border-b border-racing-line p-6 flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-racing-gold mb-2">Akun Orang Tua</p>
                                    <h3 className="font-display uppercase text-3xl">{parent.username}</h3>
                                    <p className={`mt-2 inline-block px-3 py-1 text-[10px] uppercase tracking-[0.2em] border ${parent.status === "pending" ? "border-racing-gold text-racing-golddeep bg-racing-gold/10" : parent.status === "active" ? "border-green-700 text-green-700 bg-green-50" : "border-racing-red text-racing-red bg-red-50"}`} data-testid={`parent-status-${index}`}>
                                        {parent.status === "pending" ? "Menunggu ACC" : parent.status === "active" ? "Aktif" : "Ditolak"}
                                    </p>
                                    <div className="mt-3 grid sm:grid-cols-2 gap-x-8 gap-y-1 text-sm text-racing-muted">
                                        <span>Email: {parent.email}</span>
                                        <span>WA: {parent.contact_phone || "—"}</span>
                                        <span>Ayah: {parent.father_name || "—"}</span>
                                        <span>Ibu: {parent.mother_name || "—"}</span>
                                    </div>
                                </div>
                                <div className="flex flex-wrap gap-2">
                                    {parent.status === "pending" && (
                                        <>
                                            <label className="flex items-center gap-2 border border-racing-line px-3 py-2" title="Bulan siswa mulai masuk klub — menentukan iuran bulan pertama & ceklist iuran">
                                                <span className="text-[10px] uppercase tracking-[0.15em] text-racing-muted">Bulan masuk</span>
                                                <input
                                                    type="month"
                                                    value={joinMonths[parent.id] || defaultJoinMonth}
                                                    onChange={(e) => setJoinMonths({ ...joinMonths, [parent.id]: e.target.value })}
                                                    data-testid={`join-month-${index}`}
                                                    className="bg-transparent text-sm focus:outline-none"
                                                />
                                            </label>
                                            <button onClick={() => approveParent(parent)} data-testid={`approve-parent-${index}`} className="flex items-center gap-2 bg-green-700 text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-green-800 transition-colors"><Check size={15} /> ACC</button>
                                            <button onClick={() => rejectParent(parent)} data-testid={`reject-parent-${index}`} className="flex items-center gap-2 border border-racing-red px-4 py-2 text-xs uppercase tracking-[0.2em] text-racing-red hover:bg-racing-red hover:text-white transition-colors"><X size={15} /> Tolak</button>
                                        </>
                                    )}
                                    <button onClick={() => openAccount(parent)} data-testid={`edit-parent-${index}`} className="flex items-center gap-2 border border-racing-line px-4 py-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"><PencilSimple size={15} /> Edit Akun</button>
                                    <button onClick={() => openPassword(parent)} data-testid={`password-parent-${index}`} className="flex items-center gap-2 border border-racing-line px-4 py-2 text-xs uppercase tracking-[0.2em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors"><Key size={15} /> Password</button>
                                    <button onClick={() => openAthlete(parent)} data-testid={`add-athlete-${index}`} className="flex items-center gap-2 bg-racing-red text-white px-4 py-2 text-xs uppercase tracking-[0.2em] hover:bg-racing-redhover transition-colors"><Plus size={15} /> Tambah Atlet</button>
                                    <button onClick={() => removeParent(parent)} data-testid={`delete-parent-${index}`} className="flex items-center gap-2 border border-racing-red px-4 py-2 text-xs uppercase tracking-[0.2em] text-racing-red hover:bg-racing-red hover:text-white transition-colors"><Trash size={15} /> Hapus Akun</button>
                                </div>
                            </div>

                            <div className="p-6 grid md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {(parent.children || []).map((child, childIndex) => (
                                    <article key={child.id} className="border border-racing-line bg-racing-surface p-4" data-testid={`reregistration-child-${index}-${childIndex}`}>
                                        <div className="flex gap-4">
                                            {child.photo_url ? (
                                                <img src={fileUrl(child.photo_url)} alt={child.name} className="w-20 h-20 object-cover border border-racing-line" />
                                            ) : (
                                                <div className="w-20 h-20 bg-white border border-racing-line flex items-center justify-center text-racing-muted"><UserCircle size={30} /></div>
                                            )}
                                            <div className="min-w-0 flex-1">
                                                <h4 className="font-display uppercase text-xl leading-tight">{child.name}</h4>
                                                <p className="mt-1 text-xs text-racing-muted">{child.class_name} • {child.gender || "—"}</p>
                                                <p className={`mt-1 text-[10px] uppercase tracking-[0.15em] ${child.status === "Menunggu ACC" ? "text-racing-golddeep" : child.status === "Ditolak" ? "text-racing-red" : "text-green-700"}`}>{child.status || "Aktif"}</p>
                                                <p className="mt-1 text-xs text-racing-muted">Lahir: {child.birth_place || "—"}, {child.birth_date || "—"}</p>
                                                {(child.possible_duplicates || []).length > 0 && (
                                                    <p className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-racing-golddeep" data-testid={`duplicate-warning-${index}-${childIndex}`}>
                                                        <WarningCircle size={14} /> Kemungkinan duplikat arsip
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                        <div className="mt-4 flex justify-end flex-wrap gap-2">
                                            {child.status === "Menunggu ACC" && parent.status === "active" && (
                                                <>
                                                    <label className="flex items-center gap-1.5 border border-racing-line px-2 py-1.5" title="Bulan anak mulai masuk klub — menentukan iuran bulan pertama & ceklist">
                                                        <span className="text-[9px] uppercase tracking-[0.15em] text-racing-muted">Bulan masuk</span>
                                                        <input
                                                            type="month"
                                                            value={joinMonths[child.id] || defaultJoinMonth}
                                                            onChange={(e) => setJoinMonths({ ...joinMonths, [child.id]: e.target.value })}
                                                            data-testid={`join-month-child-${index}-${childIndex}`}
                                                            className="bg-transparent text-xs focus:outline-none"
                                                        />
                                                    </label>
                                                    <button onClick={() => approveChild(child)} data-testid={`approve-child-${index}-${childIndex}`} className="inline-flex items-center gap-1.5 bg-green-700 text-white px-3 py-2 text-[10px] uppercase tracking-[0.15em] hover:bg-green-800 transition-colors">
                                                        <Check size={14} /> ACC Anak
                                                    </button>
                                                    <button onClick={() => rejectChild(child)} data-testid={`reject-child-${index}-${childIndex}`} className="inline-flex items-center gap-1.5 border border-racing-red px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-red hover:bg-racing-red hover:text-white transition-colors">
                                                        <X size={14} /> Tolak
                                                    </button>
                                                </>
                                            )}
                                            <button onClick={() => openAthlete(parent, child)} data-testid={`edit-athlete-${index}-${childIndex}`} className="p-2 border border-racing-line text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors" aria-label="Edit atlet"><PencilSimple size={15} /></button>
                                            <button onClick={() => removeAthlete(child)} data-testid={`delete-athlete-${index}-${childIndex}`} className="p-2 border border-racing-line text-racing-red hover:bg-racing-red hover:text-white transition-colors" aria-label="Hapus atlet"><Trash size={15} /></button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </section>
                    ))}
                </div>
            )}

            <Dialog open={dialog === "account"} onOpenChange={() => setDialog(null)}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-lg" data-testid="edit-account-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-xl">Edit Akun Orang Tua</DialogTitle></DialogHeader>
                    <form onSubmit={saveAccount} className="space-y-4 mt-2" data-testid="edit-account-form">
                        {[
                            ["username", "Username"],
                            ["email", "Email"],
                            ["whatsapp", "No. WhatsApp"],
                            ["father_name", "Nama Ayah"],
                            ["mother_name", "Nama Ibu"],
                        ].map(([name, label]) => (
                            <div key={name}>
                                <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">{label}</label>
                                <input required={name !== "father_name" && name !== "mother_name"} value={accountForm[name]} onChange={(e) => setAccountForm({ ...accountForm, [name]: e.target.value })} data-testid={`account-field-${name}`} className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" />
                            </div>
                        ))}
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Status Akun</label>
                            <select value={accountForm.status} onChange={(e) => setAccountForm({ ...accountForm, status: e.target.value })} data-testid="account-field-status" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red">
                                {accountForm.status === "pending" && <option value="pending">Menunggu ACC (aktifkan lewat tombol ACC)</option>}
                                <option value="active">Aktif</option>
                                <option value="rejected">Ditolak / Tidak Bisa Login</option>
                            </select>
                        </div>
                        <button disabled={saving} data-testid="save-account-button" className="w-full bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em]">{saving ? "Menyimpan..." : "Simpan Akun"}</button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === "password"} onOpenChange={() => setDialog(null)}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-md" data-testid="password-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-xl">Password Baru</DialogTitle></DialogHeader>
                    <form onSubmit={savePassword} className="space-y-4 mt-2" data-testid="password-form">
                        <p className="text-sm text-racing-muted">Kosongkan jika ingin sistem membuat password otomatis.</p>
                        <input value={passwordForm.new_password} onChange={(e) => setPasswordForm({ new_password: e.target.value })} data-testid="new-password-input" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm focus:outline-none focus:border-racing-red" placeholder="Password baru" />
                        <button disabled={saving} data-testid="save-password-button" className="w-full bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em]">{saving ? "Menyimpan..." : "Simpan Password"}</button>
                    </form>
                </DialogContent>
            </Dialog>

            <Dialog open={dialog === "athlete"} onOpenChange={() => setDialog(null)}>
                <DialogContent className="bg-white border border-racing-line text-neutral-900 max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="athlete-dialog">
                    <DialogHeader><DialogTitle className="font-display uppercase text-xl">{selectedChild ? "Edit" : "Tambah"} Atlet</DialogTitle></DialogHeader>
                    <form onSubmit={saveAthlete} className="grid sm:grid-cols-2 gap-4 mt-2" data-testid="athlete-form">
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Lengkap</label>
                            <input required value={athleteForm.name} onChange={(e) => setAthleteForm({ ...athleteForm, name: e.target.value })} data-testid="athlete-field-name" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Nama Panggilan</label>
                            <input value={athleteForm.nickname} onChange={(e) => setAthleteForm({ ...athleteForm, nickname: e.target.value })} data-testid="athlete-field-nickname" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tempat Lahir</label>
                            <input required value={athleteForm.birth_place} onChange={(e) => setAthleteForm({ ...athleteForm, birth_place: e.target.value })} data-testid="athlete-field-birth-place" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Tanggal Lahir</label>
                            <input required type="date" value={athleteForm.birth_date} onChange={(e) => setAthleteForm({ ...athleteForm, birth_date: e.target.value })} data-testid="athlete-field-birth-date" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Jenis Kelamin</label>
                            <select required value={athleteForm.gender} onChange={(e) => setAthleteForm({ ...athleteForm, gender: e.target.value })} data-testid="athlete-field-gender" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                <option value="" disabled>Pilih</option>
                                <option value="Laki-laki">Laki-laki</option>
                                <option value="Perempuan">Perempuan</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Masuk Mandalis Sejak</label>
                            <input required type="number" min="2000" max={new Date().getFullYear() + 1} value={athleteForm.joined_year} onChange={(e) => setAthleteForm({ ...athleteForm, joined_year: e.target.value })} data-testid="athlete-field-joined-year" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Kelas</label>
                            <select required value={athleteForm.class_name} onChange={(e) => setAthleteForm({ ...athleteForm, class_name: e.target.value })} data-testid="athlete-field-class" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm">
                                <option value="" disabled>Pilih kelas</option>
                                {CLASS_OPTIONS.map((option) => <option key={option} value={option}>{option}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Status</label>
                            <input value={athleteForm.status} onChange={(e) => setAthleteForm({ ...athleteForm, status: e.target.value })} data-testid="athlete-field-status" className="w-full bg-racing-surface border border-racing-line px-3 py-2.5 text-sm" />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-[10px] uppercase tracking-[0.2em] text-racing-muted mb-2">Foto Atlet</label>
                            {athleteForm.photo_url && (
                                <div className="mb-3 flex items-end gap-3">
                                    <img src={fileUrl(athleteForm.photo_url)} alt="Foto atlet" className="w-24 h-24 object-cover border border-racing-line" />
                                    <button type="button" onClick={editExistingPhoto} data-testid="athlete-photo-edit" className="border border-racing-line px-3 py-2 text-[10px] uppercase tracking-[0.15em] text-racing-muted hover:text-racing-red hover:border-racing-red transition-colors">
                                        Edit Foto
                                    </button>
                                </div>
                            )}
                            <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pickPhoto(f); }} data-testid="athlete-photo-input" className="w-full text-xs text-racing-muted" />
                            {uploading && <p className="text-xs text-racing-red mt-1">Mengunggah...</p>}
                        </div>
                        <button disabled={saving || uploading} data-testid="save-athlete-button" className="sm:col-span-2 bg-racing-red text-white py-3 text-xs uppercase tracking-[0.2em]">{saving ? "Menyimpan..." : "Simpan Atlet"}</button>
                    </form>
                </DialogContent>
            </Dialog>

            <ImageEditorModal
                open={!!photoEditor}
                imageSrc={photoEditor?.src}
                aspect={3 / 4}
                title="Edit Foto Atlet"
                onCancel={closePhotoEditor}
                onSave={onPhotoEditorSave}
            />
        </div>
    );
}
